/**
 * @fileoverview Common helpers: object flattening, endpoint URL resolution,
 * date/number/email validation, and date formatting utilities.
 * @exports Object, URL, validation, and date/format helpers
 * @author {Cognizant Technology Solutions}
 * @flow
 */
import env from '../env.json';
import getDashboardBlobUrl from '../constants/dashboardBlobUrls/getDashboardBlobUrl';
import serviceAPI from './serviceAPI';

/**
 * Recursively flattens a nested object into dot-notated keys (arrays kept).
 * @param {Object} obj The object to flatten
 * @param {string} [prefix] The key prefix for recursion
 * @returns {Object} The flattened object
 */
export const flattenObject = (obj: Object, prefix: string = '') => Object.keys(obj || {}).reduce((acc, k) => {
  const pre = prefix.length ? `${prefix}.` : '';
  if (typeof obj[k] === 'object' && Object.prototype.toString.call(obj[k]) !== '[object Array]') {
    Object.assign(acc, flattenObject(obj[k], pre + k));
  } else {
    acc[pre + k] = obj[k];
  }
  return acc;
}, {});

/**
 * Replaces all occurrences of a substring without using a global regex.
 * @param {string} value - Source string.
 * @param {string} searchValue - Substring to replace.
 * @param {string} replaceValue - Replacement text.
 * @returns {string} Updated string.
 */
export function replaceAllInString(
  value: string,
  searchValue: string,
  replaceValue: string = ''
): string {
  if (searchValue === '') {
    return value;
  }
  return value.split(searchValue).join(replaceValue);
}

/**
 * Flattens reason-code use-case data into one row per use case, annotating
 * active status and reason code details.
 * @param {Object} responseData The reason-code records with use cases
 * @returns {Array<Object>} The flattened use-case rows
 */
export const MultipleObjectFormat = (responseData: Object) => {
  const dataObject = [];
  if (responseData) {
    responseData.forEach((dObjects: Object) => {
      const useObject = dObjects.useCaseCodes;
      useObject.forEach((eachUseCase) => {
        const row = eachUseCase;
        row.isUseCaseActive = (eachUseCase.isUseCaseActive === true) ? 'Active' : 'Inactive';
        row.reasonCode = dObjects.reasonCode;
        row.reasonCodeDescription = dObjects.reasonCodeDescription;
        dataObject.push(row);
      });
    });
  }
  return dataObject;
};

/**
 * Flattens each object within a response's `objects` array in place.
 * @param {Object} responseData The response with an `objects` array
 * @returns {Object} The response with flattened `objects`
 */
export default function getflattenResponse(responseData: Object) {
  let updatedResponseData = responseData;
  const dataObject = [];

  if (responseData && typeof responseData.objects === 'object') {
    const responseDataObject = responseData.objects;

    responseDataObject.forEach((dObjects: Object, key: number) => {
      dataObject[key] = flattenObject(dObjects);
    });
    updatedResponseData = {
      ...updatedResponseData,
      objects: dataObject
    };
  }
  return updatedResponseData;
}

/**
 * This function is used for getting the api urls that varies for a functionality
 * @param {string} sourceAPI the endpoint indentifier to lookup from serviceApi.js
 * @param {string} routeParams to get the non generic url endpoints.
 * @returns {Object} the complete url for the specific app and endpoint
 */
export const getEndpointURL = (sourceAPI: string, routeParams: Object = {}) => {
  let endPointDetails = {};
  if (sourceAPI !== '' && process.env) {
    const apiEndpointDetails = serviceAPI(sourceAPI, routeParams);
    if (apiEndpointDetails.blobUrlKey) {
      endPointDetails = {
        ...apiEndpointDetails,
        url: getDashboardBlobUrl(apiEndpointDetails.blobUrlKey)
      };
    } else {
      const url = apiEndpointDetails.appName ? `REACT_APP_DPOM_${apiEndpointDetails.appName}_API_${env.APISource}` : `REACT_APP_DPOM_API_${env.APISource}`;
      endPointDetails = {
        ...apiEndpointDetails,
        url: `${process.env[url] || ''}${apiEndpointDetails.endPoint}`
      };
    }
  }
  return endPointDetails;
};

/**
 * Validates that the input string is a valid date formatted as "mm/dd/yyyy".
 * @param {string} dateString The date string to validate
 * @returns {boolean} True when the date is valid
 */
export function isValidDate(dateString: string) {
  // First check for the pattern
  if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
    return false;
  }

  // Parse the date parts to integers
  const parts = dateString.split('/');
  const day = Number.parseInt(parts[1], 10);
  const month = Number.parseInt(parts[0], 10);
  const year = Number.parseInt(parts[2], 10);

  // Check the ranges of month and year
  if (year < 1000 || year > 3000 || month === 0 || month > 12) {
    return false;
  }

  const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  // Adjust for leap years
  if (year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) {
    monthLength[1] = 29;
  }

  // Check the range of the day.
  return day > 0 && day <= monthLength[month - 1];
}

/**
 * Validates that a value is a whole number, optionally of an exact length.
 * @param {number} data The value to validate
 * @param {number} [length] Required digit length (0 to skip)
 * @returns {boolean} True when the number is valid
 */
export function isNumberValid(data: number, length: number = 0) {
  const decimalRegex = /^\d+$/;
  if (typeof data !== 'number') return false;
  if (length !== 0) {
    if (Number(data).toString().trim().length !== length) return false;
  }
  if (!decimalRegex.test(`${data}`)) return false;
  return true;
}

/**
 * Returns whether a value is defined, non-null, and non-blank.
 * @param {string} val The value to check
 * @returns {boolean} True when the value has content
 */
export function isNotnullCheck(val: string) {
  return val !== undefined && val !== null && val.trim() !== '';
}

/**
 * Validates a GAC use-case code (positive, with exactly two decimals).
 * @param {string} useCaseCode The use-case code to validate
 * @returns {boolean} True when the code is valid
 */
export function isValidGACUseCaseCode(useCaseCode: string) {
  if (useCaseCode === undefined) return false;
  if (Number(useCaseCode) < 1) return false;
  const values = useCaseCode.toString().split('.');
  const decimal = values[1];
  if (decimal === undefined) return false;
  if (decimal.length !== 2) return false;
  return true;
}

/**
 * Validates a GAC reason code (a positive one- or two-digit number).
 * @param {string} gacReasonCode The reason code to validate
 * @returns {boolean} True when the code is valid
 */
export function isValidGACReasonCode(gacReasonCode: string) {
  const decimalWithLengthTwoRegex = /^\d{1,2}$/;
  if (gacReasonCode === undefined || gacReasonCode === null || gacReasonCode === '') return false;
  if (Number(gacReasonCode) < 1) return false;
  if (!decimalWithLengthTwoRegex.test(gacReasonCode)) return false;
  return true;
}

/**
 * Validates the Tier-2 vendor value against the reason code: a vendor is only
 * allowed when the reason code is in the tier-2 options list.
 * @param {string} gacReasonCode The GAC reason code
 * @param {string} tier2Vendor The tier-2 vendor value
 * @param {Object} tierGacReasonCodeOptions Reason codes allowing tier-2 vendors
 * @returns {boolean} True when the combination is valid
 */
export function isValidTier2Vendor(
  gacReasonCode: string,
  tier2Vendor: string,
  tierGacReasonCodeOptions: Object
) {
  const code = gacReasonCode?.toString();
  const hasTier2Vendor = tier2Vendor !== undefined && tier2Vendor !== null && tier2Vendor !== '';

  // If code is not in the list and tier2Vendor exists, return false
  if (!tierGacReasonCodeOptions.includes(code) && hasTier2Vendor) {
    return false;
  }
  // If code is in the list and tier2Vendor exists, return true
  if (tierGacReasonCodeOptions.includes(code) && hasTier2Vendor) {
    return true;
  }
  // If code is not in the list and tier2Vendor does not exist, return true
  if (!tierGacReasonCodeOptions.includes(code) && !hasTier2Vendor) {
    return true;
  }
  // If code is in the list and tier2Vendor does not exist, return true
  if (tierGacReasonCodeOptions.includes(code) && !hasTier2Vendor) {
    return false;
  }
  // For all other cases, return false
  return false;
}

/**
 * Converts an Excel serial date number to an "mm/dd/yyyy" string.
 * @param {number} excelDate The Excel serial date
 * @returns {string} The formatted date, or '' for empty/invalid input
 */
export function getJsDateFromExcel(excelDate: number) {
  // JavaScript dates can be constructed by passing milliseconds
  // since the Unix epoch (January 1, 1970) example: new Date(12312512312);

  // 1. Subtract number of days between Jan 1, 1900 and Jan 1, 1970, plus 1
  // (Google "excel leap year bug")
  // 2. Convert to milliseconds.
  if (!excelDate || excelDate === 2958101 || `${excelDate}`.trim() === '') {
    return '';
  }
  const date = new Date((excelDate - (25567 + 2)) * 86400 * 1000);
  const monthText = (date.getMonth() > 8) ? (date.getMonth() + 1) : (`0${date.getMonth() + 1}`);
  const dayText = (date.getDate() > 9) ? date.getDate() : (`0${date.getDate()}`);
  const yearText = date.getFullYear();
  if (Number.isNaN(date.getTime())) {
    return `${excelDate}`;
  }
  return date ? `${monthText}/${dayText}/${yearText}` : '';
}

/**
 * This methods helps you to validate the domain of email id
 * It bascically checks if its valid nike mail id
 * returns the boolean value.
 * @param {string} emailId which needs to be validated
 * @returns {boolean} based on the validation
 */

export const validateStakeHolderEmailDomain = (emailId: string = '') => {
  let validated = false;
  const emailSubStrings = emailId.split('@');
  if (emailSubStrings.length === 2 && (emailSubStrings[1].toLowerCase() === 'nike.com')) {
    validated = true;
  }
  return validated;
};

/**
 * This methods helps you to validate email id
 * returns the boolean value.
 * @param {string} emailId which needs to be validated
 * @returns {boolean} based on the validation
 */

export const validateStakeHolderEmail = (emailId: string) => {
  let emailValidation = false;
  const emailRegx = /^[-!#$%&'*+0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;
  if (emailRegx.test(String(emailId).toLowerCase())) {
    emailValidation = true;
  }
  return emailValidation;
};
/**
 * Formats a "HH:mm" 24-hour time string into a 12-hour "h:mm AM/PM" string.
 * @param {string} time The 24-hour time string
 * @returns {string} The 12-hour formatted time
 */
function formatAMPM(time) {
  let hours = Number(time.split(':')[0]);
  const minutes = time.split(':')[1];
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours %= 12;
  hours = !hours ? 12 : hours;
  const strTime = `${hours}:${minutes} ${ampm}`;
  return strTime;
}

/**
 * Reformats an ISO date string into the requested layout, appending time for
 * the relevant types.
 * @param {string} date The source ISO date string
 * @param {string} newFormat The target date format
 * @param {string} [type] Optional context that enables time output
 * @returns {string} The reformatted date, or '-' when empty
 */
export const convertDateFormat = (date: string, newFormat: string, type?: string) => {
  let newModifiedDate = '-';
  if ((date || '').trim()) {
    const DateStr = date.split('T')[0].split('-');
    const day = DateStr[2];
    const month = DateStr[1];
    const year = DateStr[0];
    const time = ((
      type === 'poPdf'
      || type === 'sireTab'
      || type === 'upload'
      || type === 'modifiedDate'
      || type === 'lastModifiedDate'
      || type === 'featureFlagDate'
    )
      && date.split('T')[1]) ? date.split('T')[1].split('.')[0] : '';
    const modifiedTime = formatAMPM(time);
    switch (newFormat) {
      case 'yyyy/dd/MM':
        newModifiedDate = `${year}/${day}/${month}`;
        break;
      case 'dd/MM/yyyy':
        newModifiedDate = `${day}/${month}/${year}`;
        break;
      case 'yyyy/MM/dd':
        newModifiedDate = `${year}/${month}/${day}`;
        break;
      case 'MM/yyyy/dd':
        newModifiedDate = `${month}/${year}/${day}`;
        break;
      case 'dd/yyyy/MM':
        newModifiedDate = `${day}/${year}/${month}`;
        break;
      case 'MM/dd/yyyy Thh/mm/ss':
        newModifiedDate = `${month}/${day}/${year} T${time}`;
        break;
      case 'yyyy/MM/dd Thh/mm/ss':
        newModifiedDate = `${year}-${month}-${day} ${modifiedTime}`;
        break;
      default:
        newModifiedDate = `${month}/${day}/${year}`;
    }
  }
  return newModifiedDate;
};
/**
 * Returns whether a date falls outside the 1900–2100 valid range.
 * @param {string} dateString The date string to check
 * @returns {boolean} True when the date is out of range
 */
export function dateCheck(dateString: string) {
  const firstDate: Date = new Date('01/01/1900');
  const currentDate: Date = new Date(dateString);
  const lastDate: Date = new Date('01/01/2100');
  return !(currentDate <= lastDate && currentDate >= firstDate);
}

/**
 * Normalizes a date format string, uppercasing the month token to "MM".
 * @param {string} format The date format string
 * @returns {string} The normalized format
 */
export function dateStringFormatter(format: string) {
  const formatString = format.toLowerCase();
  return formatString.replace('mm', 'MM');
}

/**
 * Builds an inclusive list of year dropdown options between two years.
 * @param {number} startYear The first year (inclusive)
 * @param {number} endYear The last year (inclusive)
 * @returns {Array<Object>} Year options with `id` and `value`
 */
export function getArrayofYears(startYear: number, endYear: number) {
  const years = [];
  for (let i = startYear; i <= endYear; i += 1) {
    years.push({ id: `${i}`, value: `${i}` });
  }
  return years;
}
/**
 * Converts a date to a "yyyy-MM-dd" string without shifting the date.
 * @param {Object} inputDate The date value (Date or parsable input)
 * @returns {?string} The ISO date string, or null for empty input
 */
export const getISODate = (inputDate: Object) => {
  if (!inputDate) {
    return null;
  }
  let date = null;
  if (inputDate instanceof Date) {
    date = inputDate;
  } else {
    date = new Date(inputDate);
  }
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Returns a cryptographically-seeded random number in [0, 1).
 * @returns {number} The random value
 */
export function cryptoRand() {
  const randomBuffer = new Uint32Array(1);
  (window.crypto || window.msCrypto).getRandomValues(randomBuffer);
  return (randomBuffer[0] / (0xffffffff + 1));
}

/**
 * Converts a string to title case (first letter of each word capitalized).
 * @param {string} str The input string
 * @returns {string} The title-cased string
 */
export function titleCase(str: string) {
  return str.split(' ').map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()).join(' ');
}

/**
 * Returns whether both the primary and secondary values of a row are present.
 * @param {Object} row The data row
 * @param {Object} headCell The column config with `primary`/`secondary` keys
 * @returns {boolean} True when both values exist
 */
export function checkForPrimarySecondary(row: Object, headCell: Object) {
  return (!((row[headCell.primary] || '').toString().trim() === '' || row[headCell.primary] === undefined)) && (!((row[headCell.secondary] || '').trim() === '' || row[headCell.secondary] === undefined));
}

/**
 * Returns whether only the secondary value of a row is present.
 * @param {Object} row The data row
 * @param {Object} headCell The column config with `primary`/`secondary` keys
 * @returns {boolean} True when only the secondary value exists
 */
export function checkIfOnlySecondaryValueExist(row: Object, headCell: Object) {
  return ((row[headCell.primary] || '').trim() === '' || row[headCell.primary] === undefined) && (!((row[headCell.secondary] || '').trim() === '' || row[headCell.secondary] === undefined));
}

/**
 * Builds the display value for a primary/secondary column pair, joining both,
 * showing whichever exists, or the placeholder when both are empty.
 * @param {Object} row The data row
 * @param {Object} headCell The column config with `primary`/`secondary` keys
 * @param {string} emptyCellPlaceholder Placeholder when both values are empty
 * @returns {string} The mapped display value
 */
export function checkMapFields(row: Object, headCell: Object, emptyCellPlaceholder: string) {
  let mappedFields = '';
  const primaryVal = row[headCell.primary];
  const secondaryVal = row[headCell.secondary];
  const isPrimaryEmpty = (primaryVal === undefined || String(primaryVal).trim() === '');
  const isSecondaryEmpty = (secondaryVal === undefined || String(secondaryVal).trim() === '');
  if (!isPrimaryEmpty && !isSecondaryEmpty) {
    if (headCell.primary === 'product.consumerConstructDimensionName') {
      mappedFields = `${row[headCell.secondary]} | ${row[headCell.primary]}`;
    } else {
      mappedFields = `${row[headCell.primary]} | ${row[headCell.secondary]}`;
    }
  } else if (!isPrimaryEmpty && isSecondaryEmpty) {
    mappedFields = `${primaryVal}`;
  } else if (isPrimaryEmpty && !isSecondaryEmpty) {
    mappedFields = ` ${secondaryVal}`;
  } else {
    mappedFields = emptyCellPlaceholder;
  }
  return mappedFields;
}
/**
 * Validates a factory code (up to 6 alphanumeric characters).
 * @param {any} value The field value with a `value` property
 * @returns {string} An error message, or '' when valid
 */
export function validateFactoryCode(value: any) {
  const regex = /^[0-9a-zA-Z]{1,6}$/;
  if (!regex.test(value.value)) {
    return 'Enter correct 6 digit max Code value.';
  }
  return '';
}

/**
 * Validates that an email is a valid Nike email address.
 * @param {string} value The email to validate
 * @returns {string} An error message, or '' when valid
 */
export function validateEmailId(value: string) {
  if (!(validateStakeHolderEmail(value) && validateStakeHolderEmailDomain(value))) {
    return 'Please enter Valid Nike Email Id.';
  }
  return '';
}

/**
 * Validates a managing office code (up to 8 alphanumeric characters).
 * @param {string} value The office code to validate
 * @returns {string} An error message, or '' when valid
 */
export function validateManagingOffice(value: string) {
  const regex = /^[0-9a-zA-Z]{1,8}$/;
  if (!regex.test(value) && value) {
    return 'Enter 8 Character max length.';
  }
  return '';
}

/**
 * Validates that a value contains only alphanumerics, spaces, dots, or "_".
 * @param {string} value The value to validate
 * @returns {string} An error message, or '' when valid
 */
export function validateAlphanumeric(value: string) {
  const regex = /^[a-zA-Z0-9_. ]+$/;
  if (!regex.test(value) && value) {
    return 'Enter Valid Input Value.';
  }
  return '';
}

/**
 * Validates that at least one checkbox option is selected.
 * @param {Object} value The selected values (array-like)
 * @returns {string} An error message, or '' when valid
 */
export function validateMultiCheckbox(value: Object) {
  if (value.length < 1) {
    return 'Enter Valid Input Value.';
  }
  return '';
}

/**
 * Validates that a date-time value is present.
 * @param {Object} value The date-time value
 * @returns {string} An error message, or '' when valid
 */
export function validateDateTime(value: Object) {
  if (value === null || value === '') {
    return 'Enter Valid DateTime Value.';
  }
  return '';
}
